import { makeAutoObservable, runInAction } from 'mobx'
import { openApi } from '../../helpers/request'
import dayjs from 'dayjs'
import { FormEvent } from 'react'
import { notification } from "antd";
import i from "../../translations/i";

// Сегмент по заказам: границы включительно, null — без ограничения
export interface UsersSegment {
    orders_from: number | null
    orders_to: number | null
    days_from: number | null
    days_to: number | null
    products: number[]
    // Заказывал на одном из складов
    warehouses: number[]
    // Тип цены клиента — один из выбранных (розница, опт); без типа цены клиент считается клиентом типа по умолчанию
    price_types: number[]
}

export const emptySegment = (): UsersSegment => ({
    orders_from: null,
    orders_to: null,
    days_from: null,
    days_to: null,
    products: [],
    warehouses: [],
    price_types: [],
})

// С этого тарифа можно отправить пуш клиентам сегмента: pricing_plan считается с нуля, 3 — «Профи», четвёртый
// (то же значение на бэкенде — UserSegmentService::PUSH_PRICING_PLAN)
export const PUSH_SEGMENT_PLAN = 3

// Поля фильтров списка, которые задают аудиторию рассылки; статус не входит — пуши уходят только действующим клиентам
const SEGMENT_KEYS = ['name', 'phone', 'email', 'orders_from', 'orders_to', 'days_from', 'days_to'] as const

// Поля сегмента со списком значений: в API уходят как key[]
const SEGMENT_LIST_KEYS = ['products', 'warehouses', 'price_types']
export const isSegmentList = (key: string) => SEGMENT_LIST_KEYS.includes(key)

// Фильтры списка парами «ключ — значение»; у товаров, складов и типов цен по паре на каждый выбранный
export const segmentEntries = (filter: any): [string, string][] => {
    const entries: [string, string][] = []

    for (const key of SEGMENT_KEYS) {
        const value = filter?.[key]
        if (value !== null && value !== undefined && value !== '') entries.push([key, `${value}`])
    }
    for (const key of SEGMENT_LIST_KEYS) {
        for (const id of filter?.[key] ?? []) entries.push([key, `${id}`])
    }

    return entries
}

// Параметры для API: filter[name]=…&filter[products][]=…
export const segmentQuery = (filter: any, prefix: string = 'filter') => segmentEntries(filter)
    .map(([key, value]) => `${prefix}[${key}]${isSegmentList(key) ? '[]' : ''}=${encodeURIComponent(value)}`)
    .join('&')

export interface UserInterface {
    id: number
    firstname: string
    lastname: string
    surname: string
    email: string
    external_id: string
    phone: string
    birthday: number
    orders: any
    is_blocked: boolean
    companies: any[]
    auth_code: string | null
    fields: any
    price_type_id: number | null
    deleted_at: string | null
}

class UsersController {

    users: UserInterface[] = []
    user: UserInterface = {
        id: 0,
        firstname: '',
        lastname: '',
        surname: '',
        email: '',
        phone: '',
        external_id: '',
        birthday: 0,
        orders: {},
        is_blocked: false,
        companies: [],
        auth_code: null,
        fields: null,
        price_type_id: null,
        deleted_at: null
    }

    password = {
        password: '',
        password_repeat: ''
    }

    list = {
        limit: 20,
        offset: 0,
        count: 0,
    }

    list_filter: any = {
        id: null,
        name: null,
        email: null,
        phone: null,
        status: 'active',
        ...emptySegment(),
    }

    // Подписи выбранных товаров: в поиске их может уже не быть, а в поле фильтра название нужно
    productOptions: { value: number, label: string }[] = []
    productLabels: Record<number, string> = {}
    // Названия складов и типов цен — для условий сегмента в редакторе пуша
    warehouseLabels: Record<number, string> = {}
    priceTypeLabels: Record<number, string> = {}

    // Секция «Сегмент по заказам» по умолчанию свёрнута: ей пользуются не все. Раскрывается сама,
    // если сегмент пришёл в адресе (ссылка, «Изменить» из рассылки)
    segmentOpen: boolean = false

    bonuses_add = 1
    bonuses_delete = 1
    bonuses_add_combustion_date: any = null
    bonus_balance = 0
    bonuses = []
    bonuses_limit = 10
    bonuses_page = 1
    bonuses_total = 0
    bonus_table = 1

    modalCompany = {
        open: false,
        inn: null,
        list: []
    }

    searchTimeout: ReturnType<typeof setTimeout> | null = null
    productSearchTimeout: ReturnType<typeof setTimeout> | null = null

    // Первая загрузка списка: до ответа показываем скелетоны, а не пустое состояние
    listLoading = false

    constructor() {
        makeAutoObservable(this)
    }

    deleteBonuses(e?: FormEvent) {
        e?.preventDefault()

        let data = new FormData()
        data.append('count_bonus', `${this.bonuses_delete}`)
        data.append('user_id', `${this.user.id}`)

        openApi('post', `/user/bonus/delete`, data)
          .then(result => {
              this.bonuses_delete = 1
              this.bonus_table = 1
              this.getHistory()
          })
    }

    addBonuses(e?: FormEvent) {
        e?.preventDefault()

        let data = new FormData()
        data.append('count_bonus', `${this.bonuses_add}`)
        data.append('user_id', `${this.user.id}`)
        if(this.bonuses_add_combustion_date) data.append('combustion_date', `${this.bonuses_add_combustion_date}`)

        openApi('post', `/user/bonus/add`, data)
            .then(result => {
                this.bonuses_add = 1
                this.bonuses_add_combustion_date = null
                this.bonus_table = 1
                this.getHistory()
            })
          .catch(error => {
              console.log(error.response)
              notification.error({message: error.response.data.data.message})
          })
    }

    getList() {
        let uri = `/users?limit=${this.list.limit}&offset=${this.list.offset}`

        if(this.list_filter.id) uri += `&filter[id]=${this.list_filter.id}`
        if(this.list_filter.status) uri += `&filter[status]=${this.list_filter.status}`

        const segment = segmentQuery(this.list_filter)
        if (segment) uri += `&${segment}`

        this.listLoading = true

        openApi('get', uri)
            .then(result => {
                console.log(result)
                result = result.data
                this.list.count = result.count
                this.users = result.list
            })
            .finally(() => runInAction(() => this.listLoading = false))
    }

    getOne(id: any) {
        if(id > 0)
            openApi('get', `/users/${id}`)
                .then(result => {
                    console.log(result)
                    this.user = result.data
                    this.getHistory()
                })
              .catch(err => console.log(err.response))
    }

    exportUsers(type: string) {
        openApi('get', `/users/export/${type}`)
            .then(result => {
                 window.open(result.data)
            })
    }

    save(e: FormEvent) {
        e.preventDefault()

      console.log('1')

        const data = new FormData()
        data.append('birthday', `${this.user.birthday}`)
        data.append('phone', `${this.user.phone}`)
        data.append('email', `${this.user.email}`)
        data.append('lastname', `${this.user.lastname}`)
        data.append('firstname', `${this.user.firstname}`)
        data.append('surname', `${this.user.surname}`)
        data.append('auth_code', this.user.auth_code ? `${this.user.auth_code}` : '')
        data.append('external_id', this.user.external_id ? `${this.user.external_id}` : '')

        data.append('price_type_id', this.user.price_type_id ? `${this.user.price_type_id}` : '')

        if(this.user.id === 0 && this.password.password === '') {
            notification.error({
                message: i.t('def:errors.error'),
                description: i.t('def:input_password')
            })
            return false
        }

      console.log('2')

        if(this.password.password || this.password.password_repeat) {
            if((this.password.password !== '' || this.password.password_repeat !== '') && this.password.password === this.password.password_repeat) {
                data.append('password', this.password.password)
            } else {
                notification.error({
                    message: i.t('def:errors.error'),
                    description: i.t('def:errors.passwords')
                })
                return false
            }
        }

      console.log('3')


        openApi('post', `/user/edit/${this.user.id}`, data)
            .then(result => {
                console.log(result)
                if(this.user.id > 0) {
                   this.getOne(this.user.id)
                    notification.success({
                        message: i.t('def:success.success'),
                        description: i.t('def:success.edit')
                    })
                }
                else {
                   window.location.href = `/users/${result.data.id}`;
                }
            })
            .catch(error => console.log(error.response))
    }

    getHistory(type = 1) {
        openApi('get', `/user/bonuses/${this.user.id}/all?limit=${this.bonuses_limit}&page=${this.bonuses_page}&type_table=${type}`)
            .then(result => {
                console.log(result)
                result = result.data
                this.bonuses = result.list && Array.isArray(result.list) ? result.list : []
                this.bonus_balance = result.balance
                this.bonuses_total = result.total
            })
            .catch(error => console.log(error))
    }

    setTypeTable(e: any) {
        if(e && typeof e === 'number' && e >= 1 && e <= 2 && e !== this.bonus_table) {
            this.bonuses_page = 1
            this.bonuses_total = 0
            this.getHistory(e)
            this.bonus_table = e
        }
    }

    setFilter(name: string, value: any) {
        this.list_filter[name] = value !== '' && value !== undefined ? value : null
        this.list.offset = 0

        if (this.searchTimeout) clearTimeout(this.searchTimeout)
        this.searchTimeout = setTimeout(() => this.getList(), 500)
    }

    get segmentActive() {
        return this.segmentCount > 0
    }

    // Сколько условий сегмента задано — счётчик на свёрнутой секции
    get segmentCount() {
        const { orders_from, orders_to, days_from, days_to, products, warehouses, price_types } = this.list_filter
        const set = (value: any) => value !== null && value !== undefined
        return [
            set(orders_from) || set(orders_to),
            set(days_from) || set(days_to),
            products.length > 0,
            warehouses.length > 0,
            price_types.length > 0,
        ].filter(Boolean).length
    }

    toggleSegment() {
        this.segmentOpen = !this.segmentOpen
    }

    // Сразу несколько границ сегмента: быстрые сегменты и сброс
    setSegment(segment: Partial<UsersSegment>) {
        Object.assign(this.list_filter, segment)
        this.list.offset = 0

        if (this.searchTimeout) clearTimeout(this.searchTimeout)
        this.getList()
    }

    resetSegment() {
        this.setSegment(emptySegment())
    }

    // Фильтры живут в адресе страницы: ссылкой можно поделиться, а из рассылки — вернуться к сегменту
    readFiltersFromUrl() {
        const params = new URLSearchParams(window.location.search)

        for (const key of ['name', 'phone', 'email', 'status']) {
            const value = params.get(key)
            if (value) this.list_filter[key] = value
        }
        for (const key of ['orders_from', 'orders_to', 'days_from', 'days_to']) {
            const value = params.get(key)
            if (value !== null && value !== '' && !isNaN(Number(value))) this.list_filter[key] = Number(value)
        }

        const products = params.getAll('products').map(Number).filter(id => id > 0)
        this.list_filter.products = products
        products.forEach(id => this.loadProductLabel(id))

        this.list_filter.warehouses = params.getAll('warehouses').map(Number).filter(id => id > 0)
        this.list_filter.price_types = params.getAll('price_types').map(Number).filter(id => id > 0)
        this.segmentOpen = this.segmentActive
    }

    filtersUrl(filter: any = this.list_filter) {
        const params = new URLSearchParams(segmentEntries(filter))
        if (filter.status && filter.status !== 'active') params.set('status', filter.status)

        const query = params.toString()
        return `/users${query ? `?${query}` : ''}`
    }

    loadProductLabel(productId: number) {
        if (this.productLabels[productId]) return

        openApi('get', `/products/select?limit=1&page=1&filter[product_id]=${productId}`, {}, undefined, true)
            .then(result => runInAction(() => {
                const product = (result.data ?? [])[0]
                if (product) this.productLabels[productId] = product.name
            }))
            .catch(error => console.log(error))
    }

    // Рассылка по текущим фильтрам: условия уходят в редактор пуша вместе с названиями товаров для показа
    get pushUrl() {
        const segment: any = Object.fromEntries(segmentEntries(this.list_filter).filter(([key]) => !isSegmentList(key)))
        if (this.list_filter.products.length > 0) {
            segment.products = [...this.list_filter.products]
            segment.product_labels = Object.fromEntries(this.list_filter.products.map((id: number) => [id, this.productLabels[id] ?? `#${id}`]))
        }
        if (this.list_filter.warehouses.length > 0) {
            segment.warehouses = [...this.list_filter.warehouses]
            segment.warehouse_labels = Object.fromEntries(this.list_filter.warehouses.map((id: number) => [id, this.warehouseLabels[id] ?? `#${id}`]))
        }
        if (this.list_filter.price_types.length > 0) {
            segment.price_types = [...this.list_filter.price_types]
            segment.price_type_labels = Object.fromEntries(this.list_filter.price_types.map((id: number) => [id, this.priceTypeLabels[id] ?? `#${id}`]))
        }

        return `/pushes/0?segment=${encodeURIComponent(JSON.stringify(segment))}`
    }

    setWarehouses(ids: number[]) {
        this.setSegment({ warehouses: ids })
    }

    setWarehouseLabels(list: { id: number, name: string }[]) {
        this.warehouseLabels = Object.fromEntries(list.map(warehouse => [warehouse.id, warehouse.name]))
    }

    setPriceTypes(ids: number[]) {
        this.setSegment({ price_types: ids })
    }

    setPriceTypeLabels(list: { id: number, label: string }[]) {
        this.priceTypeLabels = Object.fromEntries(list.map(priceType => [priceType.id, priceType.label]))
    }

    setProducts(options: { value: number, label: string }[]) {
        options.forEach(option => this.productLabels[option.value] = option.label)
        this.setSegment({ products: options.map(option => option.value) })
    }

    // Поиск по названию или ID, с задержкой, чтобы не слать запрос на каждую букву
    searchProducts(search: string) {
        const query = search.trim()
        if (this.productSearchTimeout) clearTimeout(this.productSearchTimeout)

        if (query === '') {
            this.productOptions = []
            return
        }

        const filter = /^\d+$/.test(query) ? `filter[product_id]=${query}` : `filter[name]=${encodeURIComponent(query)}`

        this.productSearchTimeout = setTimeout(() => {
            openApi('get', `/products/select?limit=20&page=1&${filter}`, {}, undefined, true)
                .then(result => runInAction(() => {
                    this.productOptions = (result.data ?? []).map((product: any) => ({ value: product.id, label: product.name }))
                }))
                .catch(error => console.log(error))
        }, 300)
    }

    setStatusFilter(value: string | null) {
        this.list_filter.status = value ?? 'active'
        this.list.offset = 0

        if (this.searchTimeout) clearTimeout(this.searchTimeout)
        this.getList()
    }

    blockUser(user_id = 0) {
        if(user_id > 0) {
            openApi('post', `/users/block/${user_id}`)
              .then(result => {
                  this.getOne(user_id)
              })
              .catch(error => console.log(error))
        }
    }

    searchCompany() {
        console.log('searchCompany')
        openApi('get', `/users/search_inn?user_id=${this.user.id}&inn=${this.modalCompany.inn}`)
          .then(result => {
              console.log(result)
              this.modalCompany.list = result.data
          })
          .catch(error => console.log(error.response))
    }

    openSearchCompany() {
        this.modalCompany = {
            open: true,
            inn: null,
            list: []
        }
    }

    closeSearchCompany() {
        this.modalCompany = {
            open: false,
            inn: null,
            list: []
        }
    }

    addCompanyToUser(company: any) {
        const data = new FormData()
        data.append('user_id', `${this.user.id}`)
        data.append('inn', `${company.inn}`);

        openApi('post', '/users/company', data)
          .then(result => {
              console.log(result)
              this.closeSearchCompany()
              this.getOne(this.user.id)
          })
          .catch((error: any) => console.log(error.response))
    }

    deleteCompanyFromUser(id: number) {
        openApi('delete', `/users/company/${this.user.id}/${id}`)
          .then(result => {
              console.log(result)
              this.getOne(this.user.id)
          })
    }
}

export default new UsersController()